import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { safeParseArray, safeParseObject } from '../utils/storageUtils';

const PunishmentContext = createContext();

export const usePunishment = () => {
    const context = useContext(PunishmentContext);
    if (!context) {
        throw new Error('usePunishment must be used within a PunishmentProvider');
    }
    return context;
};

export const PunishmentProvider = ({ children }) => {
    const { user } = useAuth();
    const [punishments, setPunishments] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchPunishments = async () => {
        if (!user) {
            setLoading(false);
            return;
        }

        // Explicit column list matching the INSERT structure (lowercase in Supabase)
        const columns = [
            'id', 'userid', 'characterid', 'charactername', 'city', 'punishmenttype',
            'whitelist_affected', 'publicreason', 'internalreason', 'attachments',
            'expiresat', 'appliedby', 'appliedat', 'status', 'captureddata', 'created_at'
        ].join(',');

        let query = supabase.from('punishments').select(columns);

        // 🚀 SECURITY: Scoping search to current user ONLY if not Staff
        if (user.accessLevel === 'user') {
            query = query.eq('userid', user.discordId || user.id);
        } else {
            query = query.order('id', { ascending: false });
        }

        const { data, error } = await query;

        if (error) {
            console.error("[Supabase] Error fetching punishments:", error);
        } else {
            // Map back to camelCase for the frontend
            const mappedData = (data || []).map(p => ({
                id: p.id,
                userId: p.userid,
                characterId: p.characterid,
                characterName: p.charactername,
                city: p.city,
                punishmentType: p.punishmenttype,
                whitelistAffected: p.whitelist_affected,
                publicReason: p.publicreason,
                internalReason: p.internalreason,
                attachments: p.attachments,
                expiresAt: p.expiresat,
                appliedBy: p.appliedby,
                appliedAt: p.appliedat,
                status: p.status,
                capturedData: p.captureddata,
                createdAt: p.created_at
            }));
            setPunishments(mappedData);
        }
        setLoading(false);
    };

    // Real-time Sync with Supabase
    useEffect(() => {
        if (!user) {
            setPunishments([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        fetchPunishments();

        // 🚀 SECURITY: Scoped Real-time Subscription
        const channelOptions = { event: '*', schema: 'public', table: 'punishments' };
        if (user.accessLevel === 'user') {
            channelOptions.filter = `userid=eq.${user.discordId || user.id}`;
        }

        const channel = supabase.channel(`punishment-sync-${user.accessLevel}-${user.id}`)
            .on('postgres_changes', channelOptions, fetchPunishments)
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [user?.id, user?.accessLevel]);

    const refreshPunishments = async () => {
        setLoading(true);
        await fetchPunishments();
    };

    // Generate unique punishment ID
    const generatePunishmentId = () => {
        return `PUN-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    };

    // Add new punishment
    const addPunishment = async (punishmentData) => {
        const id = generatePunishmentId();
        const newPunishment = {
            id,
            userid: punishmentData.userId,
            characterid: punishmentData.characterId || null,
            charactername: punishmentData.characterName || '',
            city: punishmentData.city || '',
            punishmenttype: punishmentData.punishmentType,
            whitelist_affected: punishmentData.whitelistAffected || null,
            publicreason: punishmentData.publicReason,
            internalreason: punishmentData.internalReason,
            attachments: (punishmentData.attachments && punishmentData.attachments.length > 0) ? punishmentData.attachments : (punishmentData.evidencePath ? [punishmentData.evidencePath] : []),
            expiresat: punishmentData.expiresAt || null,
            appliedby: punishmentData.appliedBy,
            appliedat: new Date().toISOString(),
            status: 'active',
            // Anti-Fraud: Capture user identification data at time of punishment
            captureddata: {
                ip: punishmentData.userIP || null,
                email: punishmentData.userEmail || null,
                phone: punishmentData.userPhone || null,
                capturedAt: new Date().toISOString()
            }
        };

        try {
            const { error } = await supabase.from('punishments').insert(newPunishment);
            if (error) throw error;

            // ========== ANTI-FRAUD: Check for linked profiles when blocking ==========
            if (newPunishment.punishmenttype === 'Blacklist de Tickets' && newPunishment.captureddata) {
                checkForLinkedProfiles(newPunishment);
            }
            // =========================================================================

            return newPunishment;
        } catch (error) {
            console.error("Error adding punishment:", error);
            throw error;
        }
    };

    /**
     * Anti-Fraud: Check ALL registered users for matching data when a ban is applied
     * This runs immediately when a punishment is applied
     */
    const checkForLinkedProfiles = (punishment) => {
        try {
            console.log('[Anti-Fraud] Checking for linked profiles after punishment applied...');

            const { ip, email, phone } = punishment.captureddata || {};
            if (!ip && !email && !phone) {
                console.log('[Anti-Fraud] No captured data to match against.');
                return;
            }

            // Get all registered users
            const registeredUsers = safeParseObject('registeredUsers');
            const linkedProfiles = [];

            // Check each user for matching data
            for (const [discordId, userData] of Object.entries(registeredUsers)) {
                // Skip the banned user themselves
                if (discordId === punishment.userId) continue;

                const matchReasons = [];

                // Check IP match
                if (ip && userData.ip && ip === userData.ip) {
                    matchReasons.push('IP');
                }

                // Check email match
                if (email && userData.email && email.toLowerCase() === userData.email.toLowerCase()) {
                    matchReasons.push('Email');
                }

                // Check phone match
                if (phone && userData.phone && phone === userData.phone) {
                    matchReasons.push('Telefone');
                }

                if (matchReasons.length > 0) {
                    linkedProfiles.push({
                        discordId,
                        name: userData.name || 'Usuário Desconhecido',
                        email: userData.email,
                        ip: userData.ip,
                        matchReasons
                    });
                }
            }

            // If linked profiles found, create fraud alert ticket
            if (linkedProfiles.length > 0) {
                console.log(`[Anti-Fraud] 🚨 Found ${linkedProfiles.length} linked profile(s)!`);
                createLinkedProfileAlert(punishment, linkedProfiles);
            } else {
                console.log('[Anti-Fraud] No linked profiles found.');
            }
        } catch (error) {
            console.error('[Anti-Fraud] Error checking for linked profiles:', error);
        }
    };

    /**
     * Creates a fraud alert ticket for linked profiles found
     */
    const createLinkedProfileAlert = (punishment, linkedProfiles) => {
        try {
            const profileDetails = linkedProfiles.map((profile, index) =>
                `**Perfil ${index + 1}:** ${profile.name} (ID: ${profile.discordId}) - Correspondência: ${profile.matchReasons.join(', ')}`
            ).join('\n');

            const ticketData = {
                id: `FRAUD-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                title: `🚨 ALERTA: Perfis Vinculados a Usuário Bloqueado`,
                description: `[vermelho:🚨 ALERTA DE FRAUDE - PERFIS VINCULADOS DETECTADOS]

**O sistema detectou perfis potencialmente vinculados ao usuário recém bloqueado!**

---

[laranja:🔒 USUÁRIO BLOQUEADO]
- **Usuário:** ${punishment.characterName || 'Desconhecido'} (ID: ${punishment.userId})
- **Punição:** ${punishment.punishmentType}
- **Motivo:** ${punishment.publicReason || 'Não informado'}
- **Aplicado por:** ${punishment.appliedBy}
- **Data:** ${new Date(punishment.appliedAt).toLocaleDateString('pt-BR')}

---

[azul:🔗 PERFIS VINCULADOS ENCONTRADOS (${linkedProfiles.length})]
${profileDetails}

---

[vermelho:⚠️ AÇÃO RECOMENDADA]
1. Investigar se os perfis vinculados são contas alternativas
2. Aplicar "Blacklist de Tickets" nos perfis confirmados
3. Documentar a investigação neste ticket

*Ticket gerado automaticamente pelo Sistema de Anti-Fraude.*`,
                category: 'triagem',
                priority: 'urgent',
                status: 'aberto',
                type: 'FRAUD_ALERT',
                auxiliaryStatus: ['triagem'],
                isFraudAlert: true,
                metadata: {
                    fraudType: 'linked_profiles',
                    bannedUserId: punishment.userId,
                    linkedProfileIds: linkedProfiles.map(p => p.discordId),
                    matchCount: linkedProfiles.length,
                    detectedAt: new Date().toISOString()
                },
                userId: 'SISTEMA',
                authorName: 'Sistema Anti-Fraude',
                createdAt: new Date().toISOString(),
                responses: [],
                assignedTo: null
            };

            // Save to Supabase (Ticket Alert)
            supabase.from('tickets').insert({ id: ticketData.id, ...ticketData }).then(({ error }) => {
                if (error) console.error('[Anti-Fraud] Error saving fraud ticket:', error);
                else console.log('[Anti-Fraud] ✅ Fraud alert ticket created:', ticketData.id);
            });

            // Create staff notification
            const notification = {
                id: `FRAUD_NOTIF_${Date.now()}`,
                type: 'fraud_alert',
                title: '🚨 ALERTA: Perfis Vinculados Detectados',
                message: `${linkedProfiles.length} perfil(is) vinculado(s) ao usuário bloqueado "${punishment.characterName || punishment.userId}".`,
                priority: 'urgent',
                ticketId: ticketData.id,
                createdAt: new Date().toISOString(),
                targetRole: 'staff',
                isUrgent: true
            };

            const staffNotifications = safeParseArray('staffFraudAlerts');
            staffNotifications.unshift(notification);
            localStorage.setItem('staffFraudAlerts', JSON.stringify(staffNotifications));

            console.log('[Anti-Fraud] ✅ Staff notification created');
        } catch (error) {
            console.error('[Anti-Fraud] Error creating linked profile alert:', error);
        }
    };

    // Get punishments by user Discord ID
    const getPunishmentsByUser = (userId) => {
        return punishments.filter(p => p.userId === userId);
    };

    // Get punishments by character ID, User ID and City (Composite Key)
    const getPunishmentsByCharacter = (characterId, userId = null, city = null) => {
        return punishments.filter(p => {
            const charMatch = String(p.characterId) === String(characterId);
            const userMatch = userId ? String(p.userId) === String(userId) : true;

            // Relaxed City Match: if p.city is missing, assume it's a legacy/global match for this user
            const cityMatch = !city || !p.city || String(normalizeStr(p.city)) === String(normalizeStr(city));

            return charMatch && userMatch && cityMatch;
        });
    };

    // Get active punishments for a user
    const getActivePunishments = (userId) => {
        const now = new Date();
        return punishments.filter(p => {
            if (p.userId !== userId) return false;
            if (p.status !== 'active' && p.status !== 'under_review') return false;
            // Check expiresat or expires_at or expiresAt
            const exp = p.expiresat || p.expires_at || p.expiresAt;
            if (exp && new Date(exp) < now) {
                // Auto-expire if past expiration date
                updatePunishmentStatus(p.id, 'expired');
                return false;
            }
            return true;
        });
    };

    // Get active punishments for a specific character (Composite Key)
    const getActiveCharacterPunishments = (characterId, userId = null, city = null) => {
        const now = new Date();
        return punishments.filter(p => {
            const charMatch = String(p.characterId) === String(characterId);
            const userMatch = userId ? String(p.userId) === String(userId) : true;

            // Relaxed City Match for active punishments: legacy/missing = match
            const cityMatch = !city || !p.city || String(normalizeStr(p.city)) === String(normalizeStr(city));

            if (!charMatch || !userMatch || !cityMatch) return false;
            if (p.status !== 'active' && p.status !== 'under_review') return false;

            const exp = p.expiresat || p.expires_at || p.expiresAt;
            if (exp && new Date(exp) < now) {
                updatePunishmentStatus(p.id, 'expired');
                return false;
            }
            return true;
        });
    };

    // Helper to normalize strings for comparison
    const normalizeStr = (str) => {
        if (!str) return '';
        return String(str).trim().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase();
    };

    // Update punishment status
    const updatePunishmentStatus = async (punishmentId, newStatus) => {
        try {
            const { error } = await supabase
                .from('punishments')
                .update({ status: newStatus })
                .eq('id', punishmentId);
            if (error) throw error;
        } catch (error) {
            console.error("Error updating punishment status:", error);
        }
    };

    // Create appeal for a punishment
    const createAppeal = async (punishmentId, appealText, ticketId) => {
        try {
            // Just update the status to under_review
            // Appeal details will be tracked via the ticket system
            const { error } = await supabase
                .from('punishments')
                .update({
                    status: 'under_review'
                })
                .eq('id', punishmentId);
            if (error) throw error;
        } catch (error) {
            console.error("Error creating appeal:", error);
        }
    };

    // Check if punishment can be appealed
    const canAppeal = (punishment) => {
        if (!punishment) return false;
        if (punishment.status !== 'active') return false;
        // A punishment can be appealed if it's active and not already under review
        return true;
    };

    // Get punishment by ID
    const getPunishmentById = (punishmentId) => {
        return punishments.find(p => p.id === punishmentId);
    };

    // Revoke punishment
    const revokePunishment = async (punishmentId, revokedBy, revocationReason) => {
        try {
            const { error } = await supabase
                .from('punishments')
                .update({
                    status: 'revoked'
                    // removed revoked_by, revokedat, revocation_reason as they don't exist in DB
                })
                .eq('id', punishmentId);
            if (error) throw error;
        } catch (error) {
            console.error("Error revoking punishment:", error);
        }
    };

    // Delete punishment (permanent)
    const deletePunishment = async (punishmentId) => {
        try {
            const { error } = await supabase.from('punishments').delete().eq('id', punishmentId);
            if (error) throw error;
        } catch (error) {
            console.error("Error deleting punishment:", error);
        }
    };

    // Update punishment details
    const updatePunishment = async (punishmentId, updates) => {
        try {
            // Map camelCase updates to snake_case for Supabase
            const mappedUpdates = {};
            if (updates.status) mappedUpdates.status = updates.status;
            if (updates.duration || updates.expiresAt) {
                mappedUpdates.expiresat = updates.expiresAt;
            }
            if (updates.type || updates.punishmentType) {
                mappedUpdates.punishmenttype = updates.type || updates.punishmentType;
            }
            if (updates.reason || updates.internalReason) {
                mappedUpdates.internalreason = updates.reason || updates.internalReason;
            }
            if (updates.publicReason) {
                mappedUpdates.publicreason = updates.publicReason;
            }

            const { error } = await supabase
                .from('punishments')
                .update(mappedUpdates)
                .eq('id', punishmentId);
            if (error) throw error;
        } catch (error) {
            console.error("Error updating punishment:", error);
        }
    };

    // Get all punishments (for staff view)
    const getAllPunishments = () => {
        return punishments;
    };

    // Get punishments with filters
    const getFilteredPunishments = (filters = {}) => {
        return punishments.filter(p => {
            if (filters.status && p.status !== filters.status) return false;
            if (filters.type && p.punishmentType !== filters.type) return false;
            if (filters.appliedBy && p.appliedBy !== filters.appliedBy) return false;
            if (filters.userId && p.userId !== filters.userId) return false;
            return true;
        });
    };

    /**
     * Anti-Fraud: Check if a new user's data matches any banned user's captured data
     * @param {Object} userData - { ip, email, phone, discordId, name }
     * @returns {Object[]} - Array of matching punishments with banned user info
     */
    const checkFraudMatch = (userData) => {
        if (!userData) return [];

        const { ip, email, phone, discordId } = userData;
        const matches = [];

        // Only check active "Blacklist de Tickets" punishments
        const ticketBans = punishments.filter(p =>
            p.punishmentType === 'Blacklist de Tickets' &&
            p.status === 'active' &&
            p.userId !== discordId // Don't match the same user
        );

        for (const ban of ticketBans) {
            const captured = ban.captured_data || {};
            const matchReasons = [];

            // Check IP match (if both have IPs)
            if (ip && captured.ip && ip === captured.ip) {
                matchReasons.push('IP');
            }

            // Check email match
            if (email && captured.email && email.toLowerCase() === captured.email.toLowerCase()) {
                matchReasons.push('Email');
            }

            // Check phone match
            if (phone && captured.phone && phone === captured.phone) {
                matchReasons.push('Telefone');
            }

            if (matchReasons.length > 0) {
                matches.push({
                    punishment: ban,
                    matchReasons,
                    bannedUserId: ban.userId,
                    bannedUserName: ban.characterName || 'Usuário Desconhecido'
                });
            }
        }

        return matches;
    };

    const value = {
        punishments,
        loading,
        addPunishment,
        getPunishmentsByUser,
        getPunishmentsByCharacter,
        getActivePunishments,
        getActiveCharacterPunishments,
        updatePunishmentStatus,
        createAppeal,
        canAppeal,
        getPunishmentById,
        revokePunishment,
        updatePunishment,
        deletePunishment,
        getAllPunishments,
        getFilteredPunishments,
        checkFraudMatch,
        refreshPunishments
    };

    return (
        <PunishmentContext.Provider value={value}>
            {children}
        </PunishmentContext.Provider>
    );
};

export default PunishmentContext;
